import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Broadcast, MapPin, QrCode, Plus, CalendarBlank, Users } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const TIER_DESCRIPTIONS = {
  neighborhood: 'Block-level. Coffee shops, popups, your living room.',
  venue:        'Bars, galleries, weekly markets, weddings.',
  stadium:      'Massive crowds. Concerts, festivals, sport events.',
};

const Beacons = () => {
  const [tiers, setTiers] = useState({});
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: '', location_name: '', description: '',
    vibe: '', voice: 'aria',
    welcome_message: '', tier: 'neighborhood',
  });

  const load = () => {
    api.get('/beacons/tiers').then(({ data }) => setTiers(data.tiers));
    api.get('/beacons/mine').then(({ data }) => setMine(data.beacons || []));
  };

  useEffect(() => { load(); }, []);

  // Handle Stripe return — if ?beacon_session= present, poll fulfillment
  const [search, setSearch] = useSearchParams();
  useEffect(() => {
    const sid = search.get('beacon_session');
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 12 && !cancelled; i++) {
        try {
          const { data } = await api.get(`/beacons/checkout/status/${sid}`);
          if (data.payment_status === 'paid') {
            toast.success(`Beacon ${data.beacon?.code || ''} live`);
            load();
            break;
          }
        } catch {}
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (!cancelled) {
        setSearch((p) => { p.delete('beacon_session'); return p; }, { replace: true });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!form.label || !form.location_name) {
      toast.error('Give your beacon a label + location');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/beacons', { ...form, origin_url: window.location.origin });
      if (data.checkout_url) {
        toast.success('Redirecting to checkout…');
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(`Beacon ${data.beacon.code} forged`);
      setShowForm(false);
      setForm({ label: '', location_name: '', description: '', vibe: '', voice: 'aria', welcome_message: '', tier: 'neighborhood' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'create failed');
    } finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    try {
      await api.post(`/beacons/${id}/deactivate`);
      toast.success('Beacon deactivated');
      load();
    } catch { toast.error('Could not deactivate'); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 pb-24" data-testid="beacons-page">
      <div className="text-center mb-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-bx-volt flex items-center justify-center gap-2">
          <Broadcast size={14} weight="fill" /> bubble beacons
        </div>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-2">
          Portals in the <span className="shimmer-text">physical</span> world
        </h1>
        <p className="text-white/65 mt-3 max-w-xl mx-auto">
          Print a QR. Stick it on a wall. Anyone who scans it walks away with an ambient bubble from your venue.
        </p>
      </div>

      {!showForm ? (
        <div className="flex justify-center mb-10">
          <button data-testid="beacon-new" onClick={() => setShowForm(true)}
                  className="btn-primary px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2">
            <Plus size={16} weight="bold" /> Forge a beacon
          </button>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 mb-10 space-y-4">
          <h3 className="font-display text-xl font-bold">New beacon</h3>
          <Field label="Label (will be in bubble names)" value={form.label}
                 onChange={(v) => setForm({ ...form, label: v })}
                 placeholder="e.g. Madison Square" testid="beacon-form-label" />
          <Field label="Location" value={form.location_name}
                 onChange={(v) => setForm({ ...form, location_name: v })}
                 placeholder="e.g. NYC, 7th Ave" testid="beacon-form-location" />
          <Field label="Description (optional)" value={form.description}
                 onChange={(v) => setForm({ ...form, description: v })}
                 placeholder="What's the vibe here?" testid="beacon-form-desc" />
          <Field label="Vibe (optional)" value={form.vibe}
                 onChange={(v) => setForm({ ...form, vibe: v })}
                 placeholder="electric and bold" testid="beacon-form-vibe" />
          <Field label="Welcome whisper" value={form.welcome_message}
                 onChange={(v) => setForm({ ...form, welcome_message: v })}
                 placeholder="What should the bubble say first?" testid="beacon-form-welcome" />

          <div>
            <span className="text-xs uppercase tracking-widest text-white/65">Tier</span>
            <div className="mt-2 grid grid-cols-3 gap-3">
              {Object.entries(tiers).map(([key, t]) => (
                <button key={key} data-testid={`tier-${key}`}
                        onClick={() => setForm({ ...form, tier: key })}
                        className={`p-4 rounded-xl border text-left transition ${
                          form.tier === key ? 'border-bx-volt bg-bx-volt/10' : 'border-white/10 hover:bg-white/5'
                        }`}>
                  <div className="font-display font-bold text-sm">{t.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
                    {t.max_scans} scans · {Math.round(t.hours/24)}d
                  </div>
                  <div className="font-display font-black text-lg mt-1.5"
                       style={{ color: (t.price_usd || 0) === 0 ? '#ccff00' : '#ffd700' }}>
                    {(t.price_usd || 0) === 0 ? 'FREE' : `$${t.price_usd}`}
                  </div>
                  <div className="text-xs text-white/60 mt-2">{TIER_DESCRIPTIONS[key]}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button data-testid="beacon-submit" onClick={create} disabled={busy}
                    className="btn-primary px-6 py-3 rounded-full text-sm flex-1 disabled:opacity-50">
              {busy ? 'Forging…' :
                (tiers[form.tier]?.price_usd > 0
                  ? `Pay $${tiers[form.tier].price_usd} & forge`
                  : 'Forge beacon')}
            </button>
            <button onClick={() => setShowForm(false)} className="px-6 py-3 rounded-full text-sm glass-hi text-white/70">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Your beacons</h2>
        <span className="text-xs text-white/60">{mine.length} active</span>
      </div>

      {mine.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-white/60">
          No beacons yet. Forge one to claim a corner of the physical world.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {mine.map((b) => (
            <BeaconCard key={b.id} beacon={b} onDeactivate={deactivate} />
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/65">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           data-testid={testid}
           className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-volt text-sm" />
  </label>
);

const BeaconCard = ({ beacon, onDeactivate }) => {
  const expired = beacon.expires_at && beacon.expires_at < new Date().toISOString();
  const exhausted = (beacon.scans_count || 0) >= (beacon.max_scans || 0);
  const dead = !beacon.active || expired || exhausted;
  const pending = !beacon.paid;
  return (
    <div className={`glass rounded-2xl p-5 ${dead && !pending ? 'opacity-50' : ''} ${pending ? 'border border-bx-gold/40' : ''}`}
         data-testid={`beacon-card-${beacon.code}`}>
      {/* Cover artwork */}
      {bubbleImageSrc(beacon) && (
        <div className="relative mb-4 -mx-1 rounded-xl overflow-hidden h-32"
             data-testid={`beacon-cover-${beacon.code}`}>
          <img src={bubbleImageSrc(beacon)}
               alt={`${beacon.label} cover`}
               className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[0.3em] text-white/80">
            venue cover
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-bx-volt flex items-center gap-1">
            <Broadcast size={11} weight="fill" /> {beacon.tier}
          </div>
          <h3 className="font-display text-xl font-bold mt-1 truncate">{beacon.label}</h3>
          <div className="text-xs text-white/60 mt-1 flex items-center gap-1">
            <MapPin size={11} weight="fill" /> {beacon.location_name}
          </div>
        </div>
        {beacon.qr_image_b64 && (
          <img src={`data:image/png;base64,${beacon.qr_image_b64}`}
               alt="qr" className="h-24 w-24 rounded-lg bg-white p-1.5"
               data-testid={`beacon-qr-${beacon.code}`} />
        )}
      </div>

      <div className="mt-3 text-xs text-white/65 flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1"><QrCode size={11} weight="bold" /> /beacon/{beacon.code}</span>
        <span className="flex items-center gap-1"><Users size={11} weight="bold" /> {beacon.scans_count}/{beacon.max_scans}</span>
        <span className="flex items-center gap-1"><CalendarBlank size={11} weight="bold" />
          {beacon.expires_at ? new Date(beacon.expires_at).toLocaleDateString() : '—'}
        </span>
      </div>

      <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-bx-volt"
             style={{ width: `${Math.min(100, (beacon.scans_count / beacon.max_scans) * 100)}%` }} />
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs">
        <Link to={`/beacon/${beacon.code}`} className="text-bx-cyan font-bold"
              data-testid={`beacon-view-${beacon.code}`}>
          Preview scan page →
        </Link>
        {!dead && !pending && (
          <button onClick={() => onDeactivate(beacon.id)} className="text-white/60 hover:text-bx-magenta">
            deactivate
          </button>
        )}
        {pending && <span className="text-bx-gold">awaiting payment · ${beacon.price_usd}</span>}
        {dead && !pending && <span className="text-bx-magenta">{expired ? 'expired' : exhausted ? 'sold out' : 'inactive'}</span>}
      </div>
    </div>
  );
};

export default Beacons;
